<template>
  <div class="training-page">
    <CommonHeader />

    <div class="main-container">
      <aside class="sidebar">
        <div class="sidebar-content">
          <div class="sidebar-section">
            <div class="sidebar-section-header">
              <h3>分类</h3>
            </div>
            <div class="category-list">
              <a
                href="javascript:;"
                :class="['category-item', { active: selectedCategory === '' }]"
                @click="selectCategory('')"
              >
                <i class="el-icon-grid"></i>
                <span>全部</span>
              </a>
              <a
                v-for="category in categoryList"
                :key="category.id"
                href="javascript:;"
                :class="['category-item', { active: selectedCategory === category.id }]"
                @click="selectCategory(category.id)"
              >
                <i class="el-icon-folder"></i>
                <span>{{ category.name }}</span>
              </a>
            </div>
          </div>

          <div class="divider-line"></div>

          <div class="sidebar-section">
            <h3>热门标签</h3>
            <div class="tags-list">
              <span
                v-for="tag in hotTags"
                :key="tag.id"
                :class="['tag', { active: selectedTag === tag.id }]"
                @click="selectTag(tag.id)"
              >
                {{ tag.name }}
              </span>
            </div>
          </div>

          <div class="help-card">
            <div class="help-icon">
              <i class="el-icon-s-flag"></i>
            </div>
            <p class="help-subtitle">需要帮助?</p>
            <p class="help-title">申请部门培训新主题。</p>
            <button class="help-btn" type="button">提交申请</button>
          </div>
        </div>
      </aside>

      <main
        class="main-content"
        v-loading="loading"
        element-loading-text="加载中..."
        element-loading-spinner="el-icon-loading"
        element-loading-background="rgba(255, 255, 255, 0.8)"
      >
        <div class="content-header">
          <div>
            <h1 class="page-title">培训中心</h1>
            <p class="page-subtitle">共 {{ filteredCategories.length }} 个资料分类</p>
          </div>
        </div>

        <div class="search-filter-box">
          <div class="search-input-wrapper">
            <i class="el-icon-search search-icon"></i>
            <input
              type="text"
              placeholder="搜索分类名称或描述"
              v-model.trim="searchKey"
              @keyup.enter="handleSearch"
            />
          </div>
          <div class="divider-vertical"></div>
          <div class="sort-wrapper">
            <span class="sort-label">排序:</span>
            <div class="sort-select">
              <select v-model="sortType">
                <option value="nameAsc">名称 A-Z</option>
                <option value="nameDesc">名称 Z-A</option>
              </select>
              <i class="el-icon-arrow-down"></i>
            </div>
          </div>
          <button class="search-btn" type="button" @click="handleSearch">搜索</button>
        </div>

        <div class="folder-grid" v-if="filteredCategories.length > 0">
          <div
            v-for="category in filteredCategories"
            :key="category.id"
            class="folder-card"
            @click="openCategoryFolder(category)"
          >
            <div class="folder-icon-wrap">
              <i class="el-icon-folder-opened folder-icon"></i>
            </div>
            <h3 class="folder-title" :title="category.name">{{ category.name || '未命名分类' }}</h3>
            <p class="folder-desc" :title="category.remark || '暂无分类描述'">
              {{ category.remark || '暂无分类描述' }}
            </p>
            <div class="folder-footer">
              <span class="folder-badge">资料分类</span>
              <!-- <span class="folder-tag-count">标签 {{ getTagCount(category.id) }}</span> -->
            </div>
          </div>
        </div>

        <el-empty
          v-else-if="!loading"
          description="暂无符合条件的分类"
          :image-size="120"
        ></el-empty>
      </main>
    </div>
  </div>
</template>

<script>
import { getApeClassificationList, getApeTagByClassificationId } from '@/api/api'
import CommonHeader from '@/components/header/CommonHeader.vue'

export default {
  name: 'TrainingCenter',
  components: {
    CommonHeader
  },
  data() {
    return {
      searchKey: '',
      sortType: 'nameAsc',
      selectedCategory: '',
      selectedTag: '',
      loading: false,
      categoryList: [],
      hotTags: [],
      classificationTagMap: {}
    }
  },
  computed: {
    filteredCategories() {
      let list = [...this.categoryList]

      if (this.selectedCategory !== '') {
        list = list.filter(category => category.id === this.selectedCategory)
      }

      if (this.searchKey) {
        const keyword = this.searchKey.toLowerCase()
        list = list.filter(category => {
          const name = (category.name || '').toLowerCase()
          const remark = (category.remark || '').toLowerCase()
          return name.includes(keyword) || remark.includes(keyword)
        })
      }

      if (this.selectedTag !== '') {
        list = list.filter(category => {
          const tags = this.classificationTagMap[category.id] || []
          return tags.some(tagId => String(tagId) === String(this.selectedTag))
        })
      }

      list.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase()
        const nameB = (b.name || '').toLowerCase()
        if (nameA === nameB) {
          return 0
        }
        if (this.sortType === 'nameDesc') {
          return nameA < nameB ? 1 : -1
        }
        return nameA > nameB ? 1 : -1
      })

      return list
    }
  },
  mounted() {
    this.initPageData()
  },
  methods: {
    async initPageData() {
      this.loading = true
      try {
        await this.fetchCategoryList()
        await this.fetchHotTags('')
        await this.buildClassificationTagMap()
      } finally {
        this.loading = false
      }
    },
    async fetchCategoryList() {
      try {
        const response = await getApeClassificationList({ mark: 2 })
        if (response.code === 1000) {
          this.categoryList = response.data || []
        } else {
          this.$message.error(response.message || '获取分类列表失败')
        }
      } catch (error) {
        console.error('获取培训分类失败:', error)
        this.$message.error('获取培训分类失败')
      }
    },
    async fetchHotTags(classificationId) {
      try {
        const params = { classificationId: classificationId || '' }
        const response = await getApeTagByClassificationId(params)
        if (response.code === 1000) {
          this.hotTags = response.data || []
        } else {
          this.hotTags = []
        }
      } catch (error) {
        console.error('获取热门标签失败:', error)
        this.hotTags = []
      }
    },
    async buildClassificationTagMap() {
      const map = {}
      await Promise.all(
        this.categoryList.map(async category => {
          try {
            const response = await getApeTagByClassificationId({ classificationId: category.id })
            if (response.code === 1000) {
              map[category.id] = (response.data || []).map(tag => tag.id)
            } else {
              map[category.id] = []
            }
          } catch (error) {
            console.error('获取分类标签失败:', category.id, error)
            map[category.id] = []
          }
        })
      )
      this.classificationTagMap = map
    },
    selectCategory(categoryId) {
      this.selectedCategory = categoryId
      this.selectedTag = ''
      this.fetchHotTags(categoryId || '')
    },
    openCategoryFolder(category) {
      if (!category || !category.id) {
        return
      }
      this.$router.push({
        path: '/trainingMaterials',
        query: {
          classificationId: category.id,
          classificationName: category.name || '',
          mark: '2'
        }
      })
    },
    selectTag(tagId) {
      if (this.selectedTag === tagId) {
        this.selectedTag = ''
      } else {
        this.selectedTag = tagId
      }
    },
    handleSearch() {},
    getTagCount(categoryId) {
      const tags = this.classificationTagMap[categoryId] || []
      return tags.length
    }
  }
}
</script>

<style scoped>
.training-page {
  min-height: 100vh;
  background: #f0f2f4;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

.main-container {
  display: flex;
  max-width: 1600px;
  margin: 0 auto;
}

.sidebar {
  width: 288px;
  background: #fff;
  border-right: 1px solid #e5e7eb;
  height: calc(100vh - 65px);
  position: sticky;
  top: 65px;
  overflow-y: auto;
}

.sidebar-content {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.sidebar-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sidebar-section h3 {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
}

.category-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.category-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  text-decoration: none;
  transition: all 0.2s;
}

.category-item:hover {
  background: #f9fafb;
}

.category-item.active {
  background: rgba(26, 113, 255, 0.05);
  color: #1a71ff;
}

.divider-line {
  height: 1px;
  background: #f3f4f6;
}

.tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  padding-right: 4px;
}

.tag {
  padding: 4px 12px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
}

.tag:hover {
  border-color: #1a71ff;
  color: #1a71ff;
}

.tag.active {
  background: #1a71ff;
  border-color: #1a71ff;
  color: #fff;
}

.help-card {
  position: relative;
  background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
  border-radius: 12px;
  padding: 16px;
  color: #fff;
  overflow: hidden;
  margin-top: auto;
}

.help-icon {
  position: absolute;
  top: 8px;
  right: 8px;
  opacity: 0.2;
}

.help-subtitle {
  font-size: 12px;
  font-weight: 500;
  color: #d1d5db;
  margin: 0 0 4px 0;
}

.help-title {
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 12px 0;
}

.help-btn {
  font-size: 12px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  color: #fff;
}

.main-content {
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: 420px;
  padding: 32px 40px;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 24px;
}

.page-title {
  font-size: 36px;
  font-weight: 900;
  color: #101318;
  margin: 0;
  letter-spacing: -0.025em;
}

.page-subtitle {
  font-size: 16px;
  color: #6b7280;
  margin: 4px 0 0 0;
}

.search-filter-box {
  background: #fff;
  padding: 8px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
}

.search-input-wrapper {
  flex: 1;
  position: relative;
}

.search-input-wrapper .search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 20px;
  transition: color 0.2s;
}

.search-input-wrapper:focus-within .search-icon {
  color: #1a71ff;
}

.search-input-wrapper input {
  width: 100%;
  padding: 12px 12px 12px 44px;
  border: none;
  border-radius: 8px;
  background: #f9fafb;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;
}

.search-input-wrapper input:focus {
  background: #fff;
  box-shadow: 0 0 0 2px rgba(26, 113, 255, 0.1);
}

.divider-vertical {
  width: 1px;
  height: 32px;
  background: #e5e7eb;
}

.sort-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
}

.sort-label {
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  white-space: nowrap;
}

.sort-select {
  position: relative;
}

.sort-select select {
  appearance: none;
  background: transparent;
  border: none;
  padding: 8px 28px 8px 8px;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  cursor: pointer;
  outline: none;
}

.search-btn {
  padding: 12px 24px;
  background: #1a71ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 6px rgba(26, 113, 255, 0.2);
}

.search-btn:hover {
  background: #1557d0;
}

.folder-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
  margin-bottom: 40px;
}

.folder-card {
  background: #fff;
  border: 1px solid #edf1f5;
  border-radius: 12px;
  padding: 18px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  min-height: 180px;
}

.folder-card:hover {
  border-color: #1a71ff;
  box-shadow: 0 8px 18px rgba(26, 113, 255, 0.12);
  transform: translateY(-2px);
}

.folder-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: rgba(26, 113, 255, 0.1);
  color: #1a71ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.folder-icon {
  font-size: 26px;
}

.folder-title {
  margin: 14px 0 6px;
  font-size: 16px;
  line-height: 1.4;
  color: #111827;
  font-weight: 700;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.folder-desc {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #6b7280;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.folder-footer {
  margin-top: auto;
  padding-top: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.folder-badge {
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 999px;
  background: #eef4ff;
  color: #1a71ff;
}

.folder-tag-count {
  font-size: 12px;
  color: #8792a2;
}

@media (max-width: 1200px) {
  .folder-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 1024px) {
  .sidebar {
    display: none;
  }

  .main-content {
    padding: 24px 20px;
  }

  .folder-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .search-filter-box {
    flex-wrap: wrap;
  }

  .divider-vertical {
    display: none;
  }

  .page-title {
    font-size: 28px;
  }

  .folder-grid {
    gap: 14px;
  }
}

@media (max-width: 480px) {
  .folder-grid {
    grid-template-columns: 1fr;
  }

  .main-content {
    padding: 18px 14px;
  }
}
</style>
